<template>
  <el-form ref="form" :model="form" :rules="rules" label-width="120px">
    <el-form-item label="任务组" prop="taskGroupId">
      <el-select
        v-model="form.taskGroupId"
        filterable
        :loading="taskGroupLoading"
        placeholder="请选择月度或年度任务组"
        style="width: 360px"
        @change="handleTaskGroupChange"
      >
        <el-option v-for="group in taskGroupList" :key="group.uuid" :label="group.groupName" :value="group.uuid" />
      </el-select>
      <div class="field-hint">任务组确定执行大区、任务类型和任务周期，报告只导出该任务组内的已打卡明细。</div>
    </el-form-item>
    <el-form-item v-if="selectedTaskGroup" label="任务维度">
      <div class="task-group-dimension">
        <el-tag type="info" size="small">{{ selectedTaskGroup.zoneAreaName || '-' }}</el-tag>
        <el-tag size="small">{{ selectedTaskGroup.taskTypeName || selectedTaskGroup.taskType }}</el-tag>
        <el-tag type="success" size="small">
          {{ selectedTaskGroup.cycleTypeName || selectedTaskGroup.cycleType }}
        </el-tag>
      </div>
    </el-form-item>
    <el-form-item
      v-if="selectedTaskGroup"
      :label="form.reportPeriodType === 'month' ? '报告月份' : '报告年份'"
      prop="reportPeriodValue"
    >
      <el-date-picker
        :key="form.reportPeriodType"
        v-model="form.reportPeriodValue"
        :type="form.reportPeriodType"
        :value-format="form.reportPeriodType === 'month' ? 'yyyy-MM' : 'yyyy'"
        :format="form.reportPeriodType === 'month' ? 'yyyy年MM月' : 'yyyy年'"
        placeholder="请选择报告时间"
        style="width: 240px"
        @change="handleReportPeriodChange"
      />
    </el-form-item>
    <el-form-item
      v-if="isYearSplitMonthly"
      label="报告月份"
      prop="reportPeriodMonth"
      :rules="[{ required: true, message: '请选择报告月份', trigger: 'change' }]"
    >
      <el-select
        v-model="form.reportPeriodMonth"
        placeholder="请选择月份"
        style="width: 240px"
        @change="handleReportPeriodChange"
      >
        <el-option v-for="month in 12" :key="month" :label="month + '月'" :value="String(month).padStart(2, '0')" />
      </el-select>
    </el-form-item>
  </el-form>
</template>

<script>
import { taskService } from '@/features/gnmk/task/task.service.js';

export default {
  name: 'TaskReportForm',
  props: {
    value: { type: Object, default: () => ({}) }
  },
  data () {
    return {
      taskGroupLoading: false,
      taskGroupList: [],
      form: this.createForm(this.value),
      rules: {
        taskGroupId: [{ required: true, message: '请选择任务组', trigger: 'change' }],
        reportPeriodValue: [{ required: true, message: '请选择报告时间', trigger: 'change' }]
      }
    };
  },
  computed: {
    selectedTaskGroup () {
      return this.getSelectedTaskGroup();
    },
    isYearSplitMonthly () {
      const group = this.selectedTaskGroup;
      return !!group && group.cycleType === 'year' && (group.isSplitMonthly === 'Y' || group.isSplitMonthly === true);
    }
  },
  methods: {
    createForm (value) {
      const form = {
        taskGroupId: '',
        reportPeriodType: '',
        reportPeriodValue: '',
        reportPeriodMonth: '',
        ...(value || {})
      };
      form.reportPeriodType = form.reportPeriodType || form.periodType || '';
      form.reportPeriodValue = form.reportPeriodValue || form.periodValue || '';
      return form;
    },
    loadTaskGroups () {
      this.taskGroupLoading = true;
      taskService.queryTaskGroupPage({ pageNo: 1, pageSize: 1000 }).then(res => {
        const data = res.data || {};
        const groups = data.list || data.rows || [];
        this.taskGroupList = groups.filter(group => ['month', 'year'].includes(group.cycleType));
        this.syncTaskGroupPeriod(false);
      }).finally(() => {
        this.taskGroupLoading = false;
      });
    },
    handleTaskGroupChange () {
      this.syncTaskGroupPeriod(true);
      this.emitFormChange();
    },
    handleReportPeriodChange () {
      this.emitFormChange();
    },
    syncTaskGroupPeriod (forceReset) {
      const group = this.selectedTaskGroup;
      if (!group) return;
      const periodType = group.cycleType === 'month' ? 'month' : 'year';
      const now = new Date();
      const currentValue = periodType === 'month'
        ? `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
        : String(group.taskYear || now.getFullYear());
      const valueLength = periodType === 'month' ? 7 : 4;
      this.form.reportPeriodType = periodType;
      if (forceReset || String(this.form.reportPeriodValue || '').length !== valueLength) {
        this.form.reportPeriodValue = currentValue;
      }
      if (this.isYearSplitMonthly && (forceReset || !/^(0[1-9]|1[0-2])$/.test(String(this.form.reportPeriodMonth || '')))) {
        this.form.reportPeriodMonth = String(now.getMonth() + 1).padStart(2, '0');
      }
      if (!this.isYearSplitMonthly) {
        this.form.reportPeriodMonth = '';
      }
    },
    getSelectedTaskGroup () {
      return this.taskGroupList.find(group => group.uuid === this.form.taskGroupId) || null;
    },
    validateForm () {
      return new Promise(resolve => this.$refs.form.validate(valid => resolve(valid)));
    },
    getReportExt () {
      return { ...this.form };
    },
    emitFormChange () {
      this.$emit('change', this.getReportExt());
    }
  },
  watch: {
    value: {
      deep: true,
      handler (value) {
        this.form = this.createForm(value);
        this.$nextTick(() => this.syncTaskGroupPeriod(false));
      }
    }
  },
  created () {
    this.loadTaskGroups();
  }
};
</script>

<style scoped lang="scss">
.field-hint {
  margin-top: 6px;
  color: #909399;
  font-size: 12px;
  line-height: 1.5;
}

.task-group-dimension {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding-top: 6px;
}
</style>
